import React from 'react';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';

const StatusBadge = ({ children, type = 'default' }) => {
  const getStyles = (type) => {
    const baseStyles = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 500,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(8px)',
      transition: 'all 0.2s ease',
      minWidth: '200px',
    };

    const typeStyles = {
      default: {
        backgroundColor: 'rgba(245, 245, 245, 0.95)',
        color: '#1f1f1f',
      },
      executing: {
        backgroundColor: 'rgba(235, 245, 255, 0.95)',
        color: '#0057D1',
      },
      success: {
        backgroundColor: 'rgba(235, 255, 240, 0.95)',
        color: '#00880D',
      },
      error: {
        backgroundColor: 'rgba(255, 235, 235, 0.95)',
        color: '#CC0000',
      },
    };

    return { ...baseStyles, ...typeStyles[type] };
  };

  return (
    <div style={getStyles(type)}>
      {children}
    </div>
  );
};

const ExecutionStatusBar = ({ 
  isExecuting, 
  currentNode, 
  completionTime,
  hasError,
  progress = [],
  onViewDetails, // Add this new prop
}) => {
  const containerStyle = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 10,
    transition: 'all 0.3s ease',
    opacity: (isExecuting || completionTime) ? 1 : 0,
    transform: (isExecuting || completionTime) 
      ? 'translateY(0)' 
      : 'translateY(-16px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px'
  };

  // New style for the details button
  const detailsButtonStyle = {
    borderRadius: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px',
    fontSize: '13px',
    height: 'auto',
    pointerEvents: 'auto', // Enable clicking
    transition: 'all 0.2s ease',
  };

  const getContent = () => {
    if (isExecuting) {
      return (
        <>
          <StatusBadge type="executing">
            <Clock 
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
                animation: 'spin 2s linear infinite'
              }}
            />
            <span>
              {currentNode ? `Executing ${currentNode}...` : 'Executing workflow...'}
            </span>
          </StatusBadge>
          {progress.length > 0 && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px',
              borderRadius: '4px',
              marginTop: '4px'
            }}>
              Last completed: {progress[progress.length - 1]}
            </div>
          )}
        </>
      );
    }

    if (completionTime) {
      return (
        <>
          <StatusBadge type={hasError ? 'error' : 'success'}>
            {hasError ? (
              <>
                <AlertCircle style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                <span>Execution failed at {completionTime}</span>
              </>
            ) : (
              <>
                <CheckCircle style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                <span>
                  Execution completed at {completionTime}
                  {progress.length > 0 && ` (${progress.length} nodes executed)`}
                </span>
              </>
            )}
          </StatusBadge>
          <Button
            icon={<FileSearchOutlined />}
            onClick={onViewDetails}
            style={detailsButtonStyle}
            className="execution-details-button"
          >
            View Execution Details & Costs
          </Button>
        </>
      );
    }

    return null;
  };

  return (
    <div style={containerStyle}>
      {getContent()}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .execution-details-button:hover {
            background-color: rgba(245, 245, 245, 0.95) !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          }
        `}
      </style>
    </div>
  );
};

export default ExecutionStatusBar;